import React, { useEffect, useState } from "react";
import { Box, Typography, useMediaQuery } from "@mui/material";
import {
  CustomSectionTitle,
  CustomTextInput,
  CustomTwoColInputGroup,
  CustomCheckbox,
  CustomLoading,
  FixedActionBar,
  CustomFormNavigationGuard,
} from "@ippon/shared-components";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "@/store";
import {
  fetchDetailedEmployee,
  updateDetailedEmployee,
} from "@/store/employeeSlice";
import { fetchDetailedClient } from "@/store/clientSlice";
import { useParams } from "react-router-dom";
import { getRole, getClientID } from "@/utils/authUtils";
import { useGlobalToast } from "@/hooks/useGlobalToast";
import { useCan } from "@/hooks/useCan";
import InitialLoadError from "@/components/ErrorState/InitialLoadError";
import { getApiErrorType, type ApiErrorType } from "@/utils/apiErrorType";
import {
  shouldShowInitialLoadError,
  useStaleInitialLoadErrorToast,
} from "@/utils/initialLoadError";

type FormValues = {
  last_name: string;
  first_name: string;
  last_name_kana: string;
  first_name_kana: string;
  mail_address: string;
  role: "admin" | "general";
  is_active: boolean;
};

const EmployeeUpdate: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const isMobile = useMediaQuery("(max-width:600px)");
  const { employee_id } = useParams();
  const { showToast } = useGlobalToast();
  const [isUpdating, setIsUpdating] = useState(false);
  const userRole = getRole();
  const canEdit = userRole === "admin";
  const can = useCan();
  const clientId = getClientID();

  const {
    data: employeeData,
    loading: employeeLoading,
    loaded: employeeLoaded,
  } = useSelector((state: any) => state.employees.detailed);

  const [initialLoadError, setInitialLoadError] =
    useState<ApiErrorType | null>(null);
  const detailClientData = useSelector(
    (state: RootState) => state.clients?.detailed?.data
  );

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isDirty },
    watch,
  } = useForm<FormValues>({
    defaultValues: {
      last_name: "",
      first_name: "",
      last_name_kana: "",
      first_name_kana: "",
      mail_address: "",
      role: "general",
      is_active: true,
    },
  });

  const formValues = watch();

  const fetchDetail = React.useCallback(async () => {
    if (!employee_id) return;
    setInitialLoadError(null);
    const action = await dispatch(fetchDetailedEmployee(employee_id));
    if (!fetchDetailedEmployee.fulfilled.match(action)) {
      const payload = action.payload as { errorType?: ApiErrorType } | undefined;
      setInitialLoadError(
        payload?.errorType ?? getApiErrorType(action.payload) ?? "unknown",
      );
    }
  }, [dispatch, employee_id]);

  useEffect(() => {
    void fetchDetail();
  }, [fetchDetail]);

  useEffect(() => {
    if (clientId) {
      void dispatch(fetchDetailedClient(clientId));
    }
  }, [dispatch, clientId]);

  useEffect(() => {
    if (employeeData && Object.keys(employeeData).length > 0) {
      const init: FormValues = {
        last_name: employeeData.last_name || "",
        first_name: employeeData.first_name || "",
        last_name_kana: employeeData.last_name_kana || "",
        first_name_kana: employeeData.first_name_kana || "",
        mail_address: employeeData.mail_address || "",
        role: employeeData.is_admin ? "admin" : "general",
        is_active: employeeData.is_active !== false,
      };
      reset(init);
    }
  }, [employeeData, reset]);

  const hasEmployeeData = Boolean(employeeData && Object.keys(employeeData).length > 0);
  useStaleInitialLoadErrorToast({
    errorType: initialLoadError,
    hasCachedData: employeeLoaded || hasEmployeeData,
    dedupeKey: `employee:${employee_id ?? ""}`,
  });

  const onSubmit = async (data: FormValues) => {
    try {
      setIsUpdating(true);
      await new Promise((r) => setTimeout(r, 50));

      // 管理者のみロールと有効/無効を変更可能
      const payload = canEdit
        ? {
            client_id: clientId,
            last_name: data.last_name,
            first_name: data.first_name,
            last_name_kana: data.last_name_kana,
            first_name_kana: data.first_name_kana,
            mail_address: data.mail_address,
            role: data.role,
            is_active: data.is_active,
          }
        : {
            client_id: clientId,
            last_name: data.last_name,
            first_name: data.first_name,
            last_name_kana: data.last_name_kana,
            first_name_kana: data.first_name_kana,
            mail_address: data.mail_address,
            // 一般ユーザーはロールと有効/無効を送信しない
            role: employeeData?.is_admin ? "admin" : "general",
            is_active: employeeData?.is_active !== false,
          };

      const id = employeeData?.id || employee_id;
      const res = await dispatch(
        updateDetailedEmployee({ id, payload }) as any
      );
      if (res?.type?.endsWith("/fulfilled")) {
        await new Promise((r) => setTimeout(r, 800));
        setIsUpdating(false);
        showToast({ type: "success", message: "更新が完了しました" });
        if (employee_id) {
          void fetchDetail();
        }
      } else {
        await new Promise((r) => setTimeout(r, 800));
        setIsUpdating(false);
        showToast({ type: "error", message: "更新に失敗しました" });
      }
    } catch (e) {
      setIsUpdating(false);
      showToast({ type: "error", message: "更新に失敗しました" });
    }
  };

  const onInvalid = (formErrors: any) => {
    if (formErrors.last_name_kana || formErrors.first_name_kana) {
      showToast({
        message: "フリガナはカタカナで入力してください",
        type: "error",
      });
      return;
    }

    showToast({
      message: "入力内容を確認してください",
      type: "error",
    });
  };

  return (
    <CustomFormNavigationGuard
      hasFormChanged={isDirty}
      isSubmitting={isUpdating}
    >
      {() => (
        <Box
          pb={3}
          pt={{ xs: 2, sm: 3 }}
          component="form"
          onSubmit={handleSubmit(onSubmit, onInvalid)}
        >
          <CustomSectionTitle title="プロフィール" />

          {employeeLoading && !employeeData && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                minHeight: 400,
              }}
            >
              <CustomLoading loading={true} fullScreen={false} size="medium" />
            </Box>
          )}

          {shouldShowInitialLoadError(initialLoadError, hasEmployeeData) && (
            <InitialLoadError
              errorType={initialLoadError}
              onRetry={fetchDetail}
            />
          )}

          {(!employeeLoading || employeeData) && employeeData && (
          <Box
            className={`${isMobile ? "sp" : ""}`}
            sx={{ p: { xs: 1, sm: 3 } }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                maxWidth: 600,
              }}
            >
              <CustomTwoColInputGroup
                label="氏名"
                firstName="last_name"
                lastName="first_name"
                placeholderOne="佐藤"
                placeholderTwo="太郎"
                register={register as any}
                errors={errors as any}
                isRequired
                isModalInput={true}
                inputWidth={"600px"}
                inputWidthSp={"100%"}
                disabled={false}
              />
              <Box
                sx={{
                  "& .MuiFormHelperText-root": { mx: "10px" },
                }}
              >
                <CustomTwoColInputGroup
                  label="フリガナ"
                  firstName="last_name_kana"
                  lastName="first_name_kana"
                  placeholderOne="サトウ"
                  placeholderTwo="タロウ"
                  register={register as any}
                  errors={errors as any}
                  patternFirstName={{
                    value: /^[ァ-ヶー\s\u3000]*$/,
                    message: "カタカナで入力して下さい",
                  }}
                  patternLastName={{
                    value: /^[ァ-ヶー\s\u3000]*$/,
                    message: "カタカナで入力して下さい",
                  }}
                  inputWidth="600px"
                  inputWidthSp="100%"
                  disabled={false}
                />
              </Box>
              <Box sx={{ mb: 3 }}>
                <Box
                  sx={{
                    fontSize: "12px",
                    fontWeight: 700,
                    mb: 1.5,
                    color: "#3e3e3e",
                  }}
                >
                  ロール
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <CustomCheckbox
                    checked={formValues.role === "admin"}
                    onChange={() => {
                      if (!canEdit) return;
                      const currentRole = formValues.role;
                      const newRole =
                        currentRole === "admin" ? "general" : "admin";
                      setValue("role", newRole);
                    }}
                    size="small"
                  />
                  <Box
                    component="label"
                    sx={{
                      fontSize: "14px",
                      color: canEdit ? "#333" : "#999",
                      cursor: canEdit ? "pointer" : "default",
                      userSelect: "none",
                    }}
                    onClick={() => {
                      if (!canEdit) return;
                      const currentRole = formValues.role;
                      const newRole =
                        currentRole === "admin" ? "general" : "admin";
                      setValue("role", newRole);
                    }}
                  >
                    管理者
                  </Box>
                </Box>
              </Box>
              <Box sx={{ mb: 3 }}>
                <Box
                  sx={{
                    fontSize: "12px",
                    fontWeight: 700,
                    mb: 1.5,
                    color: "#3e3e3e",
                  }}
                >
                  利用サービス
                </Box>
                <Box
                  sx={{
                    mt: { xs: 1, sm: 2 },
                    maxWidth: "100%",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "14px",
                      color: "#333",
                    }}
                  >
                    {detailClientData?.client_services?.map((cs: any) => cs.service.name).join("，") || ""}
                  </Typography>
                </Box>
              </Box>
              <CustomTextInput
                label="メールアドレス"
                name="mail_address"
                type="email"
                placeholder="taro@example.com"
                register={register}
                errors={errors}
                isRequired
                inputWidth="600px"
                inputWidthSp="100%"
                disabled={false}
              />
            </Box>
          </Box>
          )}

          {can("employee:edit") && !employeeLoading && !shouldShowInitialLoadError(initialLoadError, hasEmployeeData) && (
            <FixedActionBar
              submitLabel="更新"
              isLoading={isUpdating}
              disabled={!isDirty || isUpdating}
            />
          )}
        </Box>
      )}
    </CustomFormNavigationGuard>
  );
};

export default EmployeeUpdate;
